<script setup>
import {useRoute} from "vue-router";
import {computed, onBeforeMount, onMounted, ref} from "vue";
import {useStore} from "vuex";
import BackButton from "@/components/layout/BackButton.vue";
import ItemNotFoundState from "@/components/states/ItemNotFoundState.vue";
import ModuleHeader from "@/components/layout/ModuleHeader.vue";
import SeminarForm from "@/components/gamification/seminar/SeminarForm.vue";
import ActivitiesForm from "@/components/gamification/seminar/ActivitiesForm.vue";
import SeminarPeriodHistory from "@/components/gamification/seminar/SeminarPeriodHistory.vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const route = useRoute()
const store = useStore()

const loading = ref(true)
const errorMessage = ref(null)
const subject = ref(null)
const selectedPeriodId = ref(null)
const periodData = ref(null)
const periodLoading = ref(false)
const periodError = ref(null)

const isNew = computed(() => route.params.seminarId === 'new')
const headerName = computed(() => isNew.value ? t('gamification.new_seminar') : store.getters['Seminars/seminar'].title)

const seminar = computed(() => store.getters['Seminars/seminar'])
const currentPeriod = computed(() => seminar.value.currentPeriod)
const isCurrentPeriodSelected = computed(() =>
    !currentPeriod.value || selectedPeriodId.value === null || selectedPeriodId.value === currentPeriod.value.id)

const periodOptions = computed(() => {
  const periods = [...(seminar.value.periods ?? [])]
  if (currentPeriod.value && !periods.some(p => p.id === currentPeriod.value.id)) {
    periods.push(currentPeriod.value)
  }
  periods.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
  return periods.map(p => ({
    value: p.id,
    title: currentPeriod.value && p.id === currentPeriod.value.id
        ? `${formatPeriod(p)} (${t('gamification.current_semester')})`
        : formatPeriod(p)
  }))
})

function formatPeriod(period){
  const typeKey = period.type === 'WINTER_SEMESTER' ? 'gamification.winter_semester' : 'gamification.summer_semester'
  return `${t(typeKey)} ${period.academicYear}`
}

async function onPeriodChange(periodId){
  periodLoading.value = true
  periodError.value = null
  try {
    if(currentPeriod.value && periodId === currentPeriod.value.id){
      periodData.value = null
      await store.dispatch('Seminars/fetchSeminarById', {seminarId: Number(route.params.seminarId)})
    } else {
      const data = await store.dispatch('Seminars/fetchPeriodData', {
        seminarId: Number(route.params.seminarId),
        periodId: periodId
      })
      if(selectedPeriodId.value === periodId){
        periodData.value = data
      }
    }
  } catch (e) {
    if(selectedPeriodId.value === periodId){
      periodError.value = t('gamification.error')
    }
  } finally {
    if(selectedPeriodId.value === periodId){
      periodLoading.value = false
    }
  }
}

async function fetchSubject(){
    try {
      subject.value = await store.dispatch('Subjects/fetchSubjectById', Number(route.params.subjectId))
      errorMessage.value = null
    } catch (e) {
      errorMessage.value = t('gamification.subject')
      loading.value = false
    }
}

async function fetchSeminar(){
  if(!isNew.value){
    try {
      await store.dispatch('Seminars/fetchSeminarById',{seminarId: Number(route.params.seminarId)})
      if(errorMessage.value !== t('gamification.subject')){ errorMessage.value = null }
    } catch (e) {
      errorMessage.value = t('gamification.seminar')
      loading.value = false
    }
  }
}

onMounted(async()=>{
  await fetchSubject()
  await fetchSeminar()
  selectedPeriodId.value = currentPeriod.value?.id ?? null
  loading.value = false
})

onBeforeMount(()=>{
  store.dispatch('Seminars/clearSeminar')
})

</script>

<template>
  <ItemNotFoundState v-if="errorMessage" :objectName="errorMessage">
    <template #backButton>
      <BackButton v-if="errorMessage === t('gamification.subject')" :target="{name: 'Subjects'}">{{$t('gamification.subjects')}}</BackButton>
      <BackButton v-if="errorMessage === t('gamification.seminar')" :target="{name: 'Gamification'}">{{$t('gamification.seminars')}}</BackButton>
    </template>
  </ItemNotFoundState>

  <Loading v-if="loading" />

  <v-container v-if="!loading && !errorMessage">
    <BackButton :target="{name: 'Gamification'}">{{ $t('gamification.seminars') }}</BackButton>
    <ModuleHeader><h1>{{ headerName }}</h1></ModuleHeader>
    <v-select
        v-if="!isNew && periodOptions.length > 1"
        v-model="selectedPeriodId"
        @update:model-value="onPeriodChange"
        :items="periodOptions"
        :label="$t('gamification.semester')"
        variant="underlined"
        class="w-33"
    />
    <seminar-form :is-new="isNew" :subject="subject" />
    <template v-if="isCurrentPeriodSelected">
      <Loading v-if="periodLoading" />
      <div v-else-if="periodError" class="text-error mt-5">{{ periodError }}</div>
      <activities-form v-else-if="!isNew" class="mt-5" />
    </template>
    <template v-else>
      <Loading v-if="periodLoading" />
      <div v-else-if="periodError" class="text-error mt-5">{{ periodError }}</div>
      <seminar-period-history v-else-if="periodData" :period-data="periodData" class="mt-5" />
    </template>
  </v-container>
</template>

<style scoped>

</style>